import { supabase } from '../lib/supabase';

export const errorMonitoringService = {
  // ============================================================================
  // ERROR LOGGING
  // ============================================================================

  async logError(errorData) {
    const { data, error } = await supabase
      ?.from('error_logs')
      ?.insert({
        error_type: errorData?.errorType,
        severity: errorData?.severity,
        error_code: errorData?.errorCode,
        error_message: errorData?.errorMessage,
        error_stack: errorData?.errorStack,
        user_email: errorData?.userEmail,
        user_session_id: errorData?.userSessionId,
        affected_module: errorData?.affectedModule,
        affected_entity_type: errorData?.affectedEntityType,
        affected_entity_id: errorData?.affectedEntityId,
        request_url: errorData?.requestUrl,
        request_method: errorData?.requestMethod,
        request_payload: errorData?.requestPayload,
        response_status: errorData?.responseStatus,
        user_agent: errorData?.userAgent,
        ip_address: errorData?.ipAddress,
        browser_info: errorData?.browserInfo,
        tags: errorData?.tags,
        metadata: errorData?.metadata
      })
      ?.select()
      ?.single();
    
    if (error) throw error;
    
    return {
      id: data?.id,
      errorType: data?.error_type,
      severity: data?.severity,
      errorMessage: data?.error_message,
      category: data?.category,
      recoveryStatus: data?.recovery_status,
      occurredAt: data?.occurred_at
    };
  },

  async getAllErrors(filters = {}) {
    let query = supabase?.from('error_logs')?.select('*');

    if (filters?.errorType) {
      query = query?.eq('error_type', filters?.errorType);
    }

    if (filters?.severity) {
      query = query?.eq('severity', filters?.severity);
    }

    if (filters?.errorStatus) {
      query = query?.eq('error_status', filters?.errorStatus);
    }

    if (filters?.affectedModule) {
      query = query?.eq('affected_module', filters?.affectedModule);
    }

    if (filters?.userEmail) {
      query = query?.ilike('user_email', `%${filters?.userEmail}%`);
    }

    if (filters?.dateFrom) {
      query = query?.gte('occurred_at', filters?.dateFrom);
    }

    if (filters?.dateTo) {
      query = query?.lte('occurred_at', filters?.dateTo);
    }

    if (filters?.searchTerm) {
      query = query?.or(`error_message.ilike.%${filters?.searchTerm}%,error_code.ilike.%${filters?.searchTerm}%`);
    }

    query = query?.order('occurred_at', { ascending: false });

    if (filters?.limit) {
      query = query?.limit(filters?.limit);
    }

    const { data, error } = await query;
    
    if (error) throw error;
    
    return data?.map(row => ({
      id: row?.id,
      errorType: row?.error_type,
      severity: row?.severity,
      errorStatus: row?.error_status,
      errorCode: row?.error_code,
      errorMessage: row?.error_message,
      errorStack: row?.error_stack,
      userEmail: row?.user_email,
      affectedModule: row?.affected_module,
      affectedEntityType: row?.affected_entity_type,
      affectedEntityId: row?.affected_entity_id,
      requestUrl: row?.request_url,
      requestMethod: row?.request_method,
      category: row?.category,
      tags: row?.tags,
      recoveryStatus: row?.recovery_status,
      recoveryAttemptedAt: row?.recovery_attempted_at,
      recoverySucceededAt: row?.recovery_succeeded_at,
      autoRecoveryEnabled: row?.auto_recovery_enabled,
      occurredAt: row?.occurred_at,
      acknowledgedAt: row?.acknowledged_at,
      resolvedAt: row?.resolved_at,
      metadata: row?.metadata
    })) || [];
  },

  async getErrorById(errorId) {
    const { data, error } = await supabase
      ?.from('error_logs')
      ?.select('*')
      ?.eq('id', errorId)
      ?.single();
    
    if (error) throw error;
    
    return {
      id: data?.id,
      errorType: data?.error_type,
      severity: data?.severity,
      errorStatus: data?.error_status,
      errorCode: data?.error_code,
      errorMessage: data?.error_message,
      errorStack: data?.error_stack,
      userEmail: data?.user_email,
      userSessionId: data?.user_session_id,
      affectedModule: data?.affected_module,
      affectedEntityType: data?.affected_entity_type,
      affectedEntityId: data?.affected_entity_id,
      requestUrl: data?.request_url,
      requestMethod: data?.request_method,
      requestPayload: data?.request_payload,
      responseStatus: data?.response_status,
      userAgent: data?.user_agent,
      ipAddress: data?.ip_address,
      browserInfo: data?.browser_info,
      category: data?.category,
      tags: data?.tags,
      recoveryStatus: data?.recovery_status,
      recoveryAttemptedAt: data?.recovery_attempted_at,
      recoverySucceededAt: data?.recovery_succeeded_at,
      autoRecoveryEnabled: data?.auto_recovery_enabled,
      metadata: data?.metadata,
      occurredAt: data?.occurred_at,
      acknowledgedAt: data?.acknowledged_at,
      resolvedAt: data?.resolved_at,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  },

  async updateErrorStatus(errorId, status, updates = {}) {
    const updateData = {
      error_status: status,
      updated_at: new Date()?.toISOString()
    };

    if (status === 'acknowledged' && !updates?.acknowledgedAt) {
      updateData.acknowledged_at = new Date()?.toISOString();
    }

    if (status === 'resolved' && !updates?.resolvedAt) {
      updateData.resolved_at = new Date()?.toISOString();
    }

    if (updates?.recoveryStatus) {
      updateData.recovery_status = updates?.recoveryStatus;
    }

    const { data, error } = await supabase
      ?.from('error_logs')
      ?.update(updateData)
      ?.eq('id', errorId)
      ?.select()
      ?.single();
    
    if (error) throw error;
    
    return {
      id: data?.id,
      errorStatus: data?.error_status,
      recoveryStatus: data?.recovery_status,
      acknowledgedAt: data?.acknowledged_at,
      resolvedAt: data?.resolved_at
    };
  },

  // ============================================================================
  // RECOVERY SUGGESTIONS
  // ============================================================================

  async getRecoverySuggestions(errorId) {
    const { data, error } = await supabase
      ?.from('error_recovery_suggestions')
      ?.select('*')
      ?.eq('error_log_id', errorId)
      ?.order('priority', { ascending: false });
    
    if (error) throw error;
    
    return data?.map(row => ({
      id: row?.id,
      errorLogId: row?.error_log_id,
      suggestionType: row?.suggestion_type,
      suggestionTitle: row?.suggestion_title,
      suggestionDescription: row?.suggestion_description,
      isAutomated: row?.is_automated,
      automationScript: row?.automation_script,
      priority: row?.priority,
      confidenceScore: row?.confidence_score,
      executed: row?.executed,
      executedAt: row?.executed_at,
      executionResult: row?.execution_result,
      executionSuccess: row?.execution_success,
      metadata: row?.metadata,
      createdAt: row?.created_at
    })) || [];
  },

  async executeRecoverySuggestion(suggestionId) {
    const { data, error } = await supabase
      ?.from('error_recovery_suggestions')
      ?.update({
        executed: true,
        executed_at: new Date()?.toISOString(),
        execution_result: 'Manual execution initiated',
        execution_success: true
      })
      ?.eq('id', suggestionId)
      ?.select()
      ?.single();
    
    if (error) throw error;
    
    return {
      id: data?.id,
      executed: data?.executed,
      executedAt: data?.executed_at,
      executionSuccess: data?.execution_success
    };
  },

  // ============================================================================
  // ERROR PATTERNS
  // ============================================================================

  async getErrorPatterns() {
    const { data, error } = await supabase
      ?.from('error_patterns')
      ?.select('*')
      ?.order('occurrence_count', { ascending: false });
    
    if (error) throw error;
    
    return data?.map(row => ({
      id: row?.id,
      patternName: row?.pattern_name,
      errorType: row?.error_type,
      errorCodePattern: row?.error_code_pattern,
      errorMessagePattern: row?.error_message_pattern,
      occurrenceCount: row?.occurrence_count,
      lastOccurredAt: row?.last_occurred_at,
      commonResolution: row?.common_resolution,
      resolutionSuccessRate: row?.resolution_success_rate,
      averageResolutionTimeMinutes: row?.average_resolution_time_minutes,
      autoRecoveryAvailable: row?.auto_recovery_available,
      autoRecoveryScript: row?.auto_recovery_script,
      metadata: row?.metadata,
      createdAt: row?.created_at
    })) || [];
  },

  // ============================================================================
  // STATISTICS
  // ============================================================================

  async getErrorStatistics(dateFrom, dateTo) {
    let query = supabase?.from('error_logs')?.select('error_type, severity, error_status, recovery_status');

    if (dateFrom) {
      query = query?.gte('occurred_at', dateFrom);
    }

    if (dateTo) {
      query = query?.lte('occurred_at', dateTo);
    }

    const { data, error } = await query;
    
    if (error) throw error;
    
    const stats = {
      totalErrors: data?.length || 0,
      activeErrors: data?.filter(e => e?.error_status === 'new' || e?.error_status === 'acknowledged')?.length || 0,
      resolvedErrors: data?.filter(e => e?.error_status === 'resolved')?.length || 0,
      criticalErrors: data?.filter(e => e?.severity === 'critical')?.length || 0,
      autoRecoveredErrors: data?.filter(e => e?.recovery_status === 'succeeded')?.length || 0,
      resolutionRate: 0,
      byErrorType: {},
      bySeverity: {
        critical: data?.filter(e => e?.severity === 'critical')?.length || 0,
        high: data?.filter(e => e?.severity === 'high')?.length || 0,
        medium: data?.filter(e => e?.severity === 'medium')?.length || 0,
        low: data?.filter(e => e?.severity === 'low')?.length || 0
      },
      byRecoveryStatus: {
        pending: data?.filter(e => e?.recovery_status === 'pending')?.length || 0,
        inProgress: data?.filter(e => e?.recovery_status === 'in_progress')?.length || 0,
        succeeded: data?.filter(e => e?.recovery_status === 'succeeded')?.length || 0,
        failed: data?.filter(e => e?.recovery_status === 'failed')?.length || 0,
        manualRequired: data?.filter(e => e?.recovery_status === 'manual_required')?.length || 0
      }
    };

    // Calculate resolution rate
    if (stats?.totalErrors > 0) {
      stats.resolutionRate = ((stats?.resolvedErrors / stats?.totalErrors) * 100)?.toFixed(2);
    }

    // Count by error type
    data?.forEach(error => {
      stats.byErrorType[error?.error_type] = (stats?.byErrorType?.[error?.error_type] || 0) + 1;
    });

    return stats;
  },

  async getSystemHealthMetrics() {
    const now = new Date();
    const last24Hours = new Date(now?.getTime() - 24 * 60 * 60 * 1000)?.toISOString();
    const last7Days = new Date(now?.getTime() - 7 * 24 * 60 * 60 * 1000)?.toISOString();

    const [stats24h, stats7d] = await Promise.all([
      this.getErrorStatistics(last24Hours, now?.toISOString()),
      this.getErrorStatistics(last7Days, now?.toISOString())
    ]);

    return {
      last24Hours: stats24h,
      last7Days: stats7d,
      systemHealth: stats24h?.criticalErrors === 0 ? 'healthy' : stats24h?.criticalErrors < 5 ? 'warning' : 'critical'
    };
  },

  // ============================================================================
  // REAL-TIME SUBSCRIPTIONS
  // ============================================================================

  subscribeToErrors(callback, filters = {}) {
    const channel = supabase
      ?.channel('error_logs_channel')
      ?.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'error_logs'
        },
        (payload) => {
          const newError = {
            id: payload?.new?.id,
            errorType: payload?.new?.error_type,
            severity: payload?.new?.severity,
            errorStatus: payload?.new?.error_status,
            errorCode: payload?.new?.error_code,
            errorMessage: payload?.new?.error_message,
            userEmail: payload?.new?.user_email,
            affectedModule: payload?.new?.affected_module,
            category: payload?.new?.category,
            recoveryStatus: payload?.new?.recovery_status,
            occurredAt: payload?.new?.occurred_at
          };

          // Apply filters
          if (filters?.severity && newError?.severity !== filters?.severity) {
            return;
          }

          if (filters?.errorType && newError?.errorType !== filters?.errorType) {
            return;
          }

          callback(newError);
        }
      )
      ?.subscribe();
    
    return channel;
  },

  unsubscribeFromErrors(channel) {
    if (channel) {
      supabase?.removeChannel(channel);
    }
  },

  // ============================================================================
  // ALERT ESCALATIONS
  // ============================================================================

  async createAlertEscalation(escalationData) {
    const { data, error } = await supabase
      ?.from('error_alert_escalations')
      ?.insert({
        error_log_id: escalationData?.errorLogId,
        escalation_level: escalationData?.escalationLevel,
        escalated_to_email: escalationData?.escalatedToEmail,
        escalated_to_role: escalationData?.escalatedToRole,
        notification_channel: escalationData?.notificationChannel,
        metadata: escalationData?.metadata
      })
      ?.select()
      ?.single();
    
    if (error) throw error;
    
    return {
      id: data?.id,
      errorLogId: data?.error_log_id,
      escalationLevel: data?.escalation_level,
      escalatedToEmail: data?.escalated_to_email,
      createdAt: data?.created_at
    };
  },

  async getAlertEscalations(errorId) {
    const { data, error } = await supabase
      ?.from('error_alert_escalations')
      ?.select('*')
      ?.eq('error_log_id', errorId)
      ?.order('escalation_level', { ascending: true });
    
    if (error) throw error;
    
    return data?.map(row => ({
      id: row?.id,
      errorLogId: row?.error_log_id,
      escalationLevel: row?.escalation_level,
      escalatedToEmail: row?.escalated_to_email,
      escalatedToRole: row?.escalated_to_role,
      notificationSent: row?.notification_sent,
      notificationSentAt: row?.notification_sent_at,
      notificationChannel: row?.notification_channel,
      acknowledged: row?.acknowledged,
      acknowledgedAt: row?.acknowledged_at,
      acknowledgedBy: row?.acknowledged_by,
      createdAt: row?.created_at
    })) || [];
  }
};

export default errorMonitoringService;
